import { purchaseDomain } from 'entrijs';
import httpClient from '../http/httpClient';
import { logError, setContext } from '../utils/sentry-logger';
import { envToBoolean } from '../utils/env';
import {
    DEFAULT_PRO_DOMAIN,
    GHOST_REDIRECT_SERVER_IP,
    ENTRI_FREE_DOMAINS_ENABLED,
} from '../utils/constants';
import { getCachedBillingData } from '../data/api';

/**
 * @typedef {object} DNSRecord
 * @property {'CNAME'|'A'} type - The DNS record type
 * @property {string} host - The hostname (e.g., 'www', '@', 'blog')
 * @property {string} value - The record value (domain or IP address)
 * @property {number} ttl - Time to live in seconds
 */

/**
 * @typedef {object} IEntriService
 * @property {function(string): Promise<void>} initializePurchaseFlow - Opens the Entri domain purchase modal
 */
/**
 * Service class for managing Entri domain purchasing functionality
 * Handles JWT token lifecycle, DNS record configuration, and purchase flow initialization
 *
 * @class EntriService
 * @implements {IEntriService}
 * @property {string} subdomain - The Ghost subdomain
 * @property {string} proDomain - The pro domain for DNS configuration
 * @property {string|null} token - Cached JWT token for Entri API
 * @property {number|null} tokenExpiresAt - Timestamp when the cached token expires
 *
 * @example
 * const entri = new EntriService('my-site');
 * await entri.initializePurchaseFlow();
 */
class EntriService {
    /**
     * Creates a new EntriService instance
     * @param {string} subdomain - Ghost subdomain (e.g., 'test-site')
     * @param {string} [proDomain=DEFAULT_PRO_DOMAIN] - The pro domain to use for DNS records
     * @throws {Error} If subdomain is not provided
     */
    constructor(subdomain, proDomain = DEFAULT_PRO_DOMAIN) {
        if (!subdomain) {
            throw new Error('Subdomain is required for EntriService');
        }

        /**
         * @type {string|null} Cached JWT token for Entri API
         */
        this.token = null;

        /**
         * @type {number|null} Timestamp when the cached token expires
         */
        this.tokenExpiresAt = null;

        /**
         * @type {string} The Ghost subdomain
         */
        this.subdomain = subdomain;

        /**
         * @type {string} The pro domain for DNS configuration
         */
        this.proDomain = proDomain;
    }

    /**
     * Fetches a JWT token from the Entri API via our backend
     * Uses got with automatic retry on network/server errors
     * @private
     * @returns {Promise<string>} JWT token for Entri API
     * @throws {Error} If token fetch fails
     */
    async _fetchToken() {
        try {
            const response = await httpClient.post('api/get-entri-token');

            // Guard against missing token in response
            if (!response?.data?.token) {
                const tokenError = new Error('Failed to fetch Entri token: No token in response');
                tokenError.responseBody = response?.data;
                tokenError.errorType = 'missing_token';
                tokenError.isEntriError = true;
                throw tokenError;
            }

            return response.data.token;
        } catch (error) {
            let tokenError;

            // Re-throw if it's already our error
            if (error.isEntriError) {
                throw error;
            }

            // Check if this is a Netlify timeout
            if (error.isNetlifyTimeout || error.response?.data?.error?.code === 'NETLIFY_TIMEOUT') {
                tokenError = new Error('Failed to fetch Entri token: Request timed out');
                tokenError.isTimeout = true;
                tokenError.errorType = 'timeout';
                tokenError.originalError = error?.response?.data?.error?.originalError;
            } else if (error.response) {
                // Server responded with error status
                const errorMessage =
                    error.response.data?.error?.message || 'Failed to fetch Entri token';
                tokenError = new Error(`Failed to fetch Entri token: ${errorMessage}`);
                tokenError.statusCode = error.response.status;
                tokenError.responseBody = error.response.data;
                tokenError.errorType = 'server_error';
            } else {
                // Network error or other error
                tokenError = new Error(`Failed to fetch Entri token: ${error.message}`);
                tokenError.originalError = error;
                tokenError.errorType = 'network_error';
            }

            tokenError.isEntriError = true;

            throw tokenError;
        }
    }

    /**
     * Gets a valid JWT token, fetching a new one if necessary
     * Tokens are cached for 55 minutes (5-minute buffer before 60-minute expiry)
     * @private
     * @returns {Promise<string>} Valid JWT token
     */
    async _getValidToken() {
        const now = Date.now();
        const fiveMinutesInMs = 5 * 60 * 1000;

        // Check if token exists and is still valid (with 5-minute buffer)
        if (this.token && this.tokenExpiresAt && this.tokenExpiresAt - fiveMinutesInMs > now) {
            return this.token;
        }

        // Fetch new token
        this.token = await this._fetchToken();
        // Set expiry to 55 minutes from now (5-minute buffer before 60-minute expiry)
        this.tokenExpiresAt = now + 55 * 60 * 1000;

        return this.token;
    }

    /**
     * Builds DNS records for Entri configuration
     * Enforces `www` subdomain since root domains aren't supported on all DNS providers
     * @private
     * @returns {Array<DNSRecord>} Conditional DNS records object for Entri
     */
    _buildDNSRecords() {
        const ghostDomain = `${this.subdomain}.${this.proDomain}`;

        return [
            {
                type: 'CNAME',
                host: 'www',
                value: ghostDomain,
                ttl: 300,
            },
            {
                type: 'A',
                host: '@',
                value: GHOST_REDIRECT_SERVER_IP,
                ttl: 300,
            },
        ];
    }

    /**
     * Builds contact details object from billing data for Entri
     * @private
     * @param {BillingData} billingData - Billing data from getPartialBillingData
     * @returns {import('entrijs').EntriConfig|undefined} Contact object for Entri or undefined if no data available
     */
    _buildContactDetails(billingData) {
        if (!billingData || !billingData.user) {
            return undefined;
        }

        const user = billingData.user;
        const contact = {};

        if (user.full_name) {
            const nameParts = user.full_name.trim().split(/\s+/);
            if (nameParts.length > 0) {
                contact.firstName = nameParts[0];
                if (nameParts.length > 1) {
                    contact.lastName = nameParts.slice(1).join(' ');
                }
            }
        }

        const email = user.billing_contact || user.email_address;
        if (email) {
            contact.email = email;
        }

        // Only return contact object if it has at least some data
        return Object.keys(contact).length > 0 ? contact : undefined;
    }

    /**
     * Builds the Entri purchase configuration
     * @private
     * @param {string} token - JWT token for Entri API
     * @param {string} applicationId - Entri application ID
     * @param {BillingData} [billingData] - Optional billing data for contact details
     * @returns {import('entrijs').EntriConfig} Configuration object for purchaseDomain
     */
    _buildPurchaseConfig(token, applicationId, billingData) {
        const dnsRecords = this._buildDNSRecords();
        const contact = this._buildContactDetails(billingData);

        /** @type {import('entrijs').EntriConfig} */
        const config = {
            sellVersion: 'v3',
            applicationId,
            token,
            dnsRecords,
            prefilledDomain: this.subdomain,
            // Callback URLs are optional and not needed - Entri uses postMessage for communication
            freeDomain: ENTRI_FREE_DOMAINS_ENABLED,
            disableMailUpsell: true,
            debugMode: envToBoolean(process.env.REACT_APP_ENTRI_DEBUG_MODE),
            whiteLabel: {
                sell: {
                    flowTarget: '_blank', // Open in new tab to avoid nested iframe navigation issues
                    overlayScreenHeight: '100%',
                    partnerLogo:
                        'https://cdn.goentri.com//uploads/9868bde9-3af1-4940-bbc2-2ecde9fe57ce.png', // The logo we uploaded to Entri
                },
                theme: {
                    primary: '#000000',
                    onPrimary: '#ffffff',
                    headerBackground: '#000000',
                },
            },
        };

        // Add contact details if available
        if (contact) {
            config.whiteLabel.sell.contact = contact;
        }

        return config;
    }
    /**
     * Opens the Entri domain purchase modal
     * Results are communicated via postMessage to the parent window
     * @returns {Promise<void>} Resolves when modal is opened
     * @throws {Error} If initialization fails
     */
    async initializePurchaseFlow() {
        // Use environment variable for application ID
        const applicationId = process.env.REACT_APP_ENTRI_APPLICATION_ID;

        if (!applicationId) {
            const error = new Error('Entri application ID is not configured');
            error.type = 'configuration';
            logError(error, {
                tags: {
                    source: 'EntriService',
                    action: 'initializePurchaseFlow',
                    error_type: 'configuration',
                },
            });
            throw error;
        }

        try {
            // Get valid token
            const token = await this._getValidToken();

            // Fetch billing data to populate contact details
            const billingData = await getCachedBillingData();

            // Build configuration
            const config = this._buildPurchaseConfig(token, applicationId, billingData);

            setContext('entri_config', {
                debugMode: config.debugMode,
                dnsRecordsCount: config.dnsRecords.length,
                subdomain: this.subdomain,
                hasContactDetails: !!config.whiteLabel.sell.contact,
            });

            // Initiate Entri purchase flow
            // This opens a modal/popup - results come via postMessage
            await purchaseDomain(config);
        } catch (error) {
            // For non-Entri errors, wrap them with context
            let errorToLog = error;
            if (!error.isEntriError) {
                errorToLog = new Error(
                    `Failed to initialize Entri purchase flow: ${error.message}`,
                );
                errorToLog.originalError = error;
                errorToLog.errorType = 'purchase_error';
            }

            // Unified logging with all available context
            logError(errorToLog, {
                tags: {
                    source: 'EntriService',
                    action: 'initializePurchaseFlow',
                    error_type: errorToLog.errorType || 'unknown',
                },
                contexts: {
                    subdomain: this.subdomain,
                    response_body: errorToLog?.responseBody,
                    status_code: errorToLog?.statusCode,
                    original_error: errorToLog?.originalError?.message,
                },
            });

            throw errorToLog;
        }
    }
}

export default EntriService;
